import { Pagination, PaginationResponse } from 'src/@types/search-params';
import {
  CheckMbfCodePackageAssigned,
  ICheckImportExcelParamsRequest,
  ICheckInputListSerial,
  ICheckInputSerialParamsRequest,
  ICheckInputTopupParams,
  IOverFlowOrderDetailResponse,
  IPackageAssigned,
  OrderRequest,
  OrderUpdate,
} from 'src/shared/interface/package_assigned';
import request from '../request';
import {
  ICheckImportOrderSubcription,
  ICheckInputListPhoneNumber,
  ICreateOrderSubcriptionParams,
  IUpdateOrderSubcriptionParams,
  PackageUpdate,
} from 'src/shared/interface/register_and_cancel_and_load';
import {
  ICheckInputSubcription,
  IDataPackageResponse,
  ISubcriptionDataOrderList,
} from 'src/shared/interface/register_and_cancel_and_load';
import { RcFile } from 'antd/es/upload';
import type { AxiosRequestConfig } from 'axios';
import { store } from 'src/redux';
import { setGlobalState } from 'src/redux/slice/globalSlice';
import { JOB_STATUS } from 'src/shared/constants';
import { sleep } from 'src/utils';

const JOB_POLL_DELAY_MS = 2000; // 2 sec
const activeJobPollingControllers = new Set<AbortController>();

const clearJobPollingState = () => {
  activeJobPollingControllers.forEach((controller) => controller.abort());
  activeJobPollingControllers.clear();
  store.dispatch(setGlobalState({ isJobProgressModalOpen: false }));
};

if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', clearJobPollingState);
  window.addEventListener('pagehide', clearJobPollingState);
}

const mapJobResultToCheckInputList = (
  result: ICheckInputSubcription | ICheckImportOrderSubcription,
  oldJobId?: string,
): ICheckInputListPhoneNumber => {
  const dataSuccess = result.dataSuccess.map((item) => ({
    ...item,
    cycle: item.cycle !== undefined ? String(item.cycle) : undefined,
  })) as IOverFlowOrderDetailResponse[];
  const dataError = result.dataError as IOverFlowOrderDetailResponse[];

  return {
    dataSuccess,
    dataError,
    oldJobId,
    phoneNumberSuccess: dataSuccess,
    phoneNumberError: dataError,
    mbfCode: dataSuccess[0]?.mbfCode ?? '',
    price: dataSuccess[0]?.price ?? 0,
  };
};

const waitForJobPolling = async ({
  jobId,
  type,
  module,
  dataPackageScope,
}: {
  jobId: string;
  type: JobType;
  module: CheckJobModule;
  dataPackageScope?: DataPackageJobScope;
}) => {
  const controller = new AbortController();
  activeJobPollingControllers.add(controller);
  let result: ICheckInputSubcription | ICheckImportOrderSubcription | undefined;

  try {
    while (!controller.signal.aborted) {
      try {
        result = await checkJob(jobId, type, module, dataPackageScope, {
          signal: controller.signal,
        });
      } catch (error: any) {
        if (
          controller.signal.aborted ||
          error?.name === 'CanceledError' ||
          error?.code === 'ERR_CANCELED'
        ) {
          return;
        }
        throw error;
      }

      if (result && ![JOB_STATUS.Pending, JOB_STATUS.Processing].includes(result.status)) {
        return result;
      }

      await sleep(JOB_POLL_DELAY_MS);
    }
  } finally {
    activeJobPollingControllers.delete(controller);
    if (activeJobPollingControllers.size === 0) {
      store.dispatch(setGlobalState({ isJobProgressModalOpen: false }));
    }
  }
};

const waitForDataPackageJob = async (
  jobId: string,
  type: Exclude<JobType, 'payment'>,
  dataPackageScope: DataPackageJobScope,
) => {
  return waitForJobPolling({
    jobId,
    type,
    module: 'dataPackage',
    dataPackageScope,
  });
};

export const waitForLoadDataJob = async (jobId: string, type: JobType) => {
  return waitForJobPolling({
    jobId,
    type,
    module: 'subscriberData',
  });
};

export const getAllRegisterPackage = async (params: any) => {
  const url = '/api/DataPackage/initiate/get-all';
  const res = await request<PaginationResponse<IPackageAssigned[]>>('get', url, params);
  if (res.isSucceeded) {
    return res.data;
  }
};
export const getAllCancelPackage = async (params: any) => {
  const url = '/api/DataPackage/cancel/get-all';
  const res = await request<PaginationResponse<IPackageAssigned[]>>('get', url, params);
  if (res.isSucceeded) {
    return res.data;
  }
};

export const checkInputPhoneRegister = async (
  type: DataPackageJobScope,
  params: ICheckInputSerialParamsRequest[],
  subType?: string,
): Promise<ICheckInputListPhoneNumber | undefined> => {
  const url = `/api/DataPackage/${type}/input${subType ? `?subType=${subType}` : ''}`;
  const res = await request<ICheckInputListPhoneNumber | ICheckInputSubcription>(
    'post',
    url,
    params,
  );
  if (res.isSucceeded) {
    if (!('jobId' in res.data)) {
      return res.data;
    }

    if (!res.data.jobId) {
      return mapJobResultToCheckInputList(res.data);
    }
    const _oldJobId = res.data.jobId;
    const jobResult = await waitForDataPackageJob(_oldJobId, 'input', type);

    return jobResult ? mapJobResultToCheckInputList(jobResult, _oldJobId) : undefined;
  }
};
export const checkInputPhoneTopup = async (params: ICheckInputTopupParams) => {
  const url = '/api/topup-provider/check';
  const res = await request<ICheckInputListPhoneNumber>('post', url, params);
  if (res.isSucceeded) {
    return res.data;
  }
};

export const checkImportExcelRegisterPackage = async (
  type: DataPackageJobScope,
  params: ICheckImportExcelParamsRequest,
): Promise<ICheckInputListPhoneNumber | undefined> => {
  const formData = new FormData();
  Object.keys(params).map((item) => {
    formData.append(`${item}`, params[`${item as keyof typeof params}`]);
  });
  const url = `/api/DataPackage/${type}/import`;
  const res = await request<ICheckInputListPhoneNumber | ICheckImportOrderSubcription>(
    'post',
    url,
    formData,
  );
  if (res.isSucceeded) {
    if (!('jobId' in res.data)) {
      return res.data;
    }

    if (!res.data.jobId) {
      return mapJobResultToCheckInputList(res.data);
    }

    const jobResult = await waitForDataPackageJob(res.data.jobId, 'input', type);

    return jobResult ? mapJobResultToCheckInputList(jobResult) : undefined;
  }
};
export const checkImportExcelTopup = async (params: ICheckImportExcelParamsRequest) => {
  const formData = new FormData();
  Object.keys(params).map((item) => {
    const value = params[`${item as keyof typeof params}`];
    // Form data phải chuyển về dạng cùng key mới truyền array được
    if (Array.isArray(value)) {
      value.forEach((val) => {
        formData.append(`${item}[]`, val);
      });
    } else {
      formData.append(`${item}`, value);
    }
  });
  const url = `/api/topup-provider/import`;
  const res = await request<ICheckInputListPhoneNumber>('post', url, formData);
  if (res.isSucceeded) {
    return res.data;
  }
};

export const createRegisterPackage = async (type: DataPackageJobScope, params: any) => {
  const url = `/api/DataPackage/${type}/create-order`;
  const res = await request('post', url, params);
  if (res.isSucceeded) {
    if (res.data?.jobId) {
      const jobResult = await waitForDataPackageJob(res.data.jobId, 'create', type);

      return {
        ...res.data,
        id: jobResult?.orderId ?? res.data.id,
      };
    }

    return res.data;
  }
};
export const createTopupPackage = async (params: any) => {
  const url = '/api/topup-provider/create-order';
  const res = await request('post', url, params);
  if (res.isSucceeded) {
    return res.data;
  }
};
export const updateTopupPackage = async (params: any) => {
  const url = '/api/topup-provider/edit-order';
  const res = await request('post', url, params);
  if (res.isSucceeded) {
    return res.data;
  }
};
export const getDetailPackage = async (type: string, id: string) => {
  const url = `/api/DataPackage/${type}/get-detail/${id}`;
  const res = await request<IPackageAssigned>('get', url);
  if (res.isSucceeded) {
    return res.data;
  }
};
export const getDetailTopup = async (id: string) => {
  const url = `/api/topup-provider/get-detail/${id}`;
  const res = await request<IPackageAssigned>('get', url);
  if (res.isSucceeded) {
    return res.data;
  }
};

export const registerAndCancelPackage = async (type: string, data: OrderRequest) => {
  const url = `/api/DataPackage/${type}/order`;
  const res = await request<IPackageAssigned>('post', url, data);
  if (res.isSucceeded) {
    return res.data;
  }
};
export const confirmTopupPackage = async (data: OrderRequest) => {
  const url = `/api/topup-provider/order`;
  const res = await request<IPackageAssigned>('post', url, data);
  if (res.isSucceeded) {
    return res.data;
  }
};
export const cancelTopupPackage = async (id: string) => {
  const url = `/api/topup-provider/cancel-order/${id}`;
  const res = await request<IPackageAssigned>('put', url);
  if (res.isSucceeded) {
    return res.data;
  }
};
export const deleteRegisterPackage = async (orderId: string) => {
  const url = `/api/DataPackage/delete-order/${orderId}`;
  const res = await request<IPackageAssigned>('delete', url);
  if (res.isSucceeded) {
    return res.data;
  }
};
export const deleteTopup = async (orderId: string) => {
  const url = `/api/topup-provider/delete-order/${orderId}`;
  const res = await request<IPackageAssigned>('delete', url);
  if (res.isSucceeded) {
    return res.data;
  }
};
export const getPackageAssigned = async (simType?: number) => {
  const url = `api/Provider/register-package/data-package/${simType}`;
  const res = await request<string[]>('get', url);
  if (res.isSucceeded) {
    return res.data;
  }
};
export const getRegisterPackage = async (
  type?: string,
  actionCode?: string,
  subType?: string,
  actionType?: string,
  isHideBundle?: boolean,
) => {
  const url =
    `/api/DataPackage?cosType=${type}` +
    (actionCode ? `&actionCode=${actionCode}` : '') +
    (subType ? `&subType=${subType}` : '') +
    (actionType ? `&actionType=${actionType}` : '') +
    (isHideBundle ? `&isHideBundle=${isHideBundle}` : '');
  const res = await request<IOverFlowOrderDetailResponse[]>('get', url);
  if (res.isSucceeded) {
    return res.data;
  }
};
export const getAllDataPackage = async () => {
  const url = `/api/DataPackage`;
  const res = await request<string[]>('get', url);
  if (res.isSucceeded) {
    return res.data;
  }
};
export const updatePackage = async (type: DataPackageJobScope, data: PackageUpdate) => {
  const url = `/api/DataPackage/${type}/edit-order`;
  const res = await request<OrderUpdate & { jobId?: string }>('post', url, data);
  if (res.isSucceeded) {
    if (res.data?.jobId) {
      return waitForDataPackageJob(res.data.jobId, 'update', type);
    }

    return res.data;
  }
};
export const retryPackageAssigned = async (data: OrderUpdate) => {
  const url = `/api/Provider/retry-order`;
  const res = await request<IPackageAssigned>('post', url, data);
  if (res.isSucceeded) {
    return res.data;
  }
};
export const checkMbfCodePackageAssigned = async (data: CheckMbfCodePackageAssigned) => {
  const url = `/api/Provider/register-package/check-list-serial-sim`;
  const res = await request<ICheckInputListPhoneNumber>('post', url, data);
  if (res.isSucceeded) {
    return res.data;
  }
};
export const exportTemplatePackageAssigned = async (type: number) => {
  const url = `/api/Provider/export-excel-template?type=${type}`;
  const res = await request<ICheckInputListSerial>('get', url);
  if (res.isSucceeded) {
    return res.data;
  }
};
export const cancelRegisterPackage = async (orderId: string) => {
  const url = `/api/DataPackage/cancel-order/` + orderId;
  const res = await request<ICheckInputListSerial>('post', url);
  if (res.isSucceeded) {
    return res.data;
  }
};
export const getRegisterAndCancelHistory = async (params: any) => {
  const url = `/api/DataPackage/history`;
  const res = await request('get', url, params);
  if (res.isSucceeded) {
    return res.data;
  }
};
export const getAssignedPackageHistory = async (params: any) => {
  const url = `/api/Provider/register-package/history`;
  const res = await request('get', url, params);
  if (res.isSucceeded) {
    return res.data;
  }
};
export const getTopupHistory = async (params: any) => {
  const url = `/api/topup-provider/get-history-topup`;
  const res = await request('get', url, params);
  if (res.isSucceeded) {
    return res.data;
  }
};
export const getTopupName = async () => {
  const url = `/api/topup-provider/discount?codeProvider=MOBI`;
  const res = await request<string[]>('get', url);
  if (res.isSucceeded) {
    return res.data;
  }
};
export const getAllTopup = async (params: any) => {
  const url = '/api/topup-provider/get-all';
  const res = await request<PaginationResponse<IPackageAssigned[]>>('get', url, params);
  if (res.isSucceeded) {
    return res.data;
  }
};

export const getAllDataPackageNew = async () => {
  const url = 'api/DataPackage/all';
  const res = await request('get', url);
  if (res.isSucceeded) {
    return res.data;
  }
};
export const getAllReportDataPackageNew = async () => {
  const url = 'api/Provider/report/data-usage/datapackage';
  const res = await request('get', url);
  if (res.isSucceeded) {
    return res.data;
  }
};

// Lấy danh sách gói
export const getAllDataPackageLoad = async () => {
  const url = 'api/SubscriberData/data-package/all';
  const res = await request<IDataPackageResponse[]>('get', url);
  if (res.isSucceeded) {
    return res.data;
  }
};
//Kiểm tra danh sách thuê bao
export const checkListSubcriptionInput = async (data: ICheckInputSerialParamsRequest[]) => {
  const url = 'api/SubscriberData/check/input';
  const res = await request<ICheckInputSubcription>('post', url, data);
  if (res.isSucceeded) {
    return res.data;
  }
};
// Kiểm tra danh sách thuê bao (import)
export const checkListSubcriptionImport = async (params: {
  orderId: string;
  mbfCode: string;
  excelFile: RcFile;
}) => {
  const formData = new FormData();
  formData.append('excelFile', params.excelFile);
  formData.append('orderId', params.orderId);
  formData.append('mbfCode', params.mbfCode);
  const url = `api/SubscriberData/check/import`;
  const res = await request<ICheckImportOrderSubcription>('post', url, formData);
  if (res.isSucceeded) {
    return res.data;
  }
};

//Kiểm tra job status
export type JobType = 'input' | 'create' | 'payment' | 'update'; // Nhập thuê bao / Tạo đơn hàng / Thanh toán / Cập nhật đơn hàng
export type CheckJobModule = 'subscriberData' | 'dataPackage'; // Nạp data / Đăng ký gói & Hủy gói
export type DataPackageJobScope = 'initiate' | 'cancel'; // Đăng ký gói / Hủy gói

export const checkJob = async (
  jobId: string,
  type: JobType,
  module: CheckJobModule = 'subscriberData',
  dataPackageScope: DataPackageJobScope = 'initiate',
  config?: AxiosRequestConfig,
): Promise<ICheckInputSubcription | ICheckImportOrderSubcription | undefined> => {
  const subscriberDataUrlMap: Record<JobType, string> = {
    input: `api/SubscriberData/check/job/${jobId}`,
    create: `api/SubscriberData/create-order/status/${jobId}`,
    payment: `api/SubscriberData/recheck-and-pay/status/${jobId}`,
    update: `api/SubscriberData/edit-order/status/${jobId}`,
  };

  const dataPackageUrlMap: Partial<Record<JobType, string>> = {
    input: `api/DataPackage/${dataPackageScope}/input/status/${jobId}`,
    create: `api/DataPackage/${dataPackageScope}/create-order/status/${jobId}`,
    update: `api/DataPackage/${dataPackageScope}/edit-order/status/${jobId}`,
  };

  const url = module === 'dataPackage' ? dataPackageUrlMap[type] : subscriberDataUrlMap[type];
  if (!url) {
    return;
  }

  const res = await request<ICheckInputSubcription | ICheckImportOrderSubcription>(
    'get',
    url,
    undefined,
    {
      ...config,
      headers: {
        ...config?.headers,
        'Skip-Loading': true,
      },
    },
  );
  if (res.isSucceeded) {
    const isProcessing = [JOB_STATUS.Pending, JOB_STATUS.Processing].includes(res.data.status);
    const isDone = res.data.status === JOB_STATUS.Done;
    store.dispatch(
      setGlobalState({
        isJobProgressModalOpen: isProcessing || isDone,
        jobProcessedCount: Number(res.data.processedCount ?? 0),
        jobTotalCount: Number(res.data.totalCount ?? 0),
        jobStatusName: res.data.statusName ?? '',
        jobStatus: res.data.status,
      }),
    );

    if (isDone) {
      await sleep(1000);
      store.dispatch(setGlobalState({ isJobProgressModalOpen: false }));
      activeJobPollingControllers.clear();
    }

    return res.data;
  }
};
// Tạo đơn hàng
export const createOrderSubcription = async (data: ICreateOrderSubcriptionParams) => {
  const url = 'api/SubscriberData/create-order';
  const res = await request('post', url, data);
  if (res.isSucceeded) {
    return res.data;
  }
};
// Cập nhật đơn hàng
export const updateOrderSubcription = async (data: IUpdateOrderSubcriptionParams) => {
  const url = 'api/SubscriberData/edit-order';
  const res = await request('post', url, data);
  if (res.isSucceeded) {
    return res.data;
  }
};
// Check đơn hàng trước khi thanh toán
export const recheckOrderSubcription = async (data: { orderId: string }) => {
  const url = 'api/SubscriberData/recheck-order-payment';
  const res = await request('post', url, data);
  if (res.isSucceeded) {
    return res.data;
  }
};
// Lấy danh sách PTTT - Localpay
export const getAllDataPaymentMethodLoad = async () => {
  const url = 'api/SubscriberData/payment-method';
  const res = await request<string[]>('get', url);
  if (res.isSucceeded) {
    return res.data;
  }
};
// Thanh toán
export const paymentSubcriptionData = async (data: { orderId: string; paymentMethod: string }) => {
  const url = 'api/SubscriberData/payment';
  const res = await request('post', url, data);
  if (res.isSucceeded) {
    return res.data;
  }
};
// Kiểm tra phương thức thanh toán
export const checkPaymentMethod = async () => {
  const url = 'api/SubscriberData/check-payment-method';
  const res = await request('get', url);
  if (res.isSucceeded) {
    return res.data;
  }
};
// Nạp data
export const submitSubcriptionData = async (data: { orderId: string }) => {
  const url = 'api/SubscriberData/submit';
  const res = await request('post', url, data);
  if (res.isSucceeded) {
    return res.data;
  }
};
// Lấy danh sách đơn hàng
export const getAllSubcriptionOrders = async (params: Pagination) => {
  const url = 'api/SubscriberData/get-all';
  const res = await request<PaginationResponse<ISubcriptionDataOrderList[]>>('get', url, params);
  if (res.isSucceeded) {
    return res.data;
  }
};
// Lấy chi tiết đơn hàng
export const getDetailSubcriptionOrder = async (orderId: string) => {
  const url = `api/SubscriberData/get-detail/${orderId}`;
  const res = await request('get', url);
  if (res.isSucceeded) {
    return res.data;
  }
};
// Hủy đơn hàng
export const cancelSubcriptionOrder = async (orderId: string) => {
  const url = `api/SubscriberData/cancel-order/${orderId}`;
  const res = await request('post', url);
  if (res.isSucceeded) {
    return res.data;
  }
};
